<template src="../templates/favourites-list.html" />

<script setup>

import { onMounted, ref, computed } from "vue";
import { useStorage } from "../composables/useStorage";
import FavouriteIcon from "./FavouriteIcon.vue";
import FavouritesMap from "./FavouritesMap.vue";

const { itemsPerPage } = defineProps({
    itemsPerPage: {
        type: Number,
        required: true,
    },
})

const favItems = ref([]);
const showMap = ref(true);
const isLoading = ref(true);

const currentPage = ref(1);

const removeFavBtnClasses = ['button', 'remove', 'favourites'];

// Compute the favourites to display on the current page
const paginatedFavourites = computed(() => {
    const startIndex = (currentPage.value - 1) * itemsPerPage;
    const endIndex = currentPage.value * itemsPerPage;
    const items = favItems.value.slice(startIndex, endIndex);
    return items;
});

// Compute the total number of pages
const totalPages = computed(() => Math.ceil(favItems.value.length / itemsPerPage));

const totalItems = favItems.value.length;

const totalPageNumbers = computed(() => {
    const num = [];
    for (let i = 1; i <= totalPages.value; i++) {
        num.push(i);
    }
    return num;
});

const isPaginated = computed(() => totalPages.value > 1);

const startingItemPositionOnPage = computed(() => currentPage.value * itemsPerPage - (itemsPerPage - 1));
const endingItemPositionOnPage = computed(() => currentPage.value === totalPages.value ? favItems.value.length : currentPage.value * itemsPerPage);

// Functions to navigate to previous and next pages
const previousPage = () => {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
};

const goToPage = (page) => {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
    }
};

const nextPage = () => {
    if (currentPage.value < totalPages.value) {
        currentPage.value++;
    }
};

const toggleMap = () => {
    showMap.value = !showMap.value;
}

const registerFavouriteRemove = () => {
    window.addEventListener('favourite-remove', (event) => {
        if (event.detail.removeWithoutConfirm || confirm('Are you sure? You won\'t be able to revert this!')) {
            const favId = event.detail.favId;
            favItems.value = favItems.value.filter(favourite => favourite.favId !== favId);

            const favourites = useStorage('favourites');
            const index = favourites.indexOf(favId);
            if (index > -1) {
                favourites.splice(index, 1);
                useStorage('favourites', favourites);

                window.dispatchEvent(new Event('storage'));
            }

            if (paginatedFavourites.value.length === 0 && currentPage.value > 1) {
                previousPage();
            }
        }
    });
}

const removeAllFavourites = () => {
    const event = new CustomEvent('remove-all-favourites');
    window.dispatchEvent(event);
}

const registerRemoveAllFavourites = () => {
    window.addEventListener('remove-all-favourites', (event) => {
        if (confirm('Are you sure? You won\'t be able to revert this!')) {
            const favourites = useStorage('favourites');
            favourites.splice(0);

            favItems.value = [];

            useStorage('favourites', favourites);
            // Emit a storage event to notify other components about the change
            window.dispatchEvent(new Event('storage'));
        }
    })
}

onMounted(async () => {
    const favIds = useStorage('favourites');

    let baseUrl = location.pathname;
    if (baseUrl.endsWith("/")) {

        baseUrl = baseUrl.slice(0, -1);

    }

    const response = await fetch(`${baseUrl}/fetch-favs/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ favIds }),
    });
    isLoading.value = false;
    const json = await response.json();

    favItems.value = json.results;

    registerFavouriteRemove();
    registerRemoveAllFavourites();

    // remove items from the list
    const itemsToRemove = json.removeItems;
    if (itemsToRemove.length) {
        itemsToRemove.forEach((favID) => {

            const event = new CustomEvent('favourite-remove', {
                detail: {
                    favId: favID,
                    removeWithoutConfirm: true,
                }
            });
            window.dispatchEvent(event);

        });
    }
});


const hasListings = computed(() => {

    return favItems.value.filter((fav) => fav.type === 'Listing').length > 0;

});


</script>

